import React from 'react';
import moment from "moment";
import {Button} from "react-bootstrap";
import Grid from "../../components/grid/grid";
import {parseReportResponse} from '../../components/grid/reportResponse';
import DashboardMenu from "../../components/dashboardMenu/dashboardMenu";
import NumberFilter from '@inovua/reactdatagrid-community/NumberFilter'
import DateFilter from '@inovua/reactdatagrid-community/DateFilter'
import SelectFilter from '@inovua/reactdatagrid-community/SelectFilter'
import useApi, {CONTROLLER} from "../../hooks/useApi";
import Field from "../../components/form/field";
import {StoreContext} from "../../store";
import {useHistory} from "react-router-dom";
import {prettyAmount} from "../../utils/amount";
// import ChooseColumns from './chooseColumns/chooseColumns';

const escapeCsvValue = (value) => {
  if (value == null) return '';
  const text = String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const buildCsv = ({ columns, rows }) => {
  const headerRow = columns.map((c) => escapeCsvValue(c.header || c.name)).join(',');
  const dataRows = rows.map((row) => columns.map((c) => escapeCsvValue(row[c.name])).join(','));
  return [headerRow, ...dataRows].join('\n');
};

const downloadTextFile = ({ contents, filename, mimeType }) => {
  const blob = new Blob([contents], { type: mimeType || 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

const getHeaderName = (name) => (name || '').replace(/([A-Z]+)/g, ' $1').trim();

const gainLossTextColor = (ratio) => {
  if (!Number.isFinite(ratio)) {
    return undefined;
  }
  const maxAbsPct = 10;
  const absPct = Math.min(Math.abs(ratio * 100), maxAbsPct);
  const t = absPct / maxAbsPct;
  const lightness = 85 - t * 25;
  const hue = ratio >= 0 ? 120 : 0;
  return `hsl(${hue}, 70%, ${lightness}%)`;
};

const formatGainLossPct = (ratio) => {
  if (!Number.isFinite(ratio)) {
    return '';
  }
  const pct = ratio * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
};

const formatExportValue = (name, value) => {
  if (value == null) return '';
  if (name && String(name).endsWith('PredictedGainLossPct')) {
    const num = Number(value);
    return Number.isFinite(num) ? formatGainLossPct(num) : '';
  }
  if (name && String(name).endsWith('Date')) {
    try {
      return value ? moment.utc(value).format('YYYY-MM-DD') : '';
    } catch (e) {
      return String(value);
    }
  }
  return value;
};

const buildDataSearchRowId = (row) => {
  const ticker = row?.Ticker || 'UNKNOWN';
  const date = row?.Date ? moment.utc(row.Date).format('YYYY-MM-DD') : 'UNKNOWN_DATE';
  return `${date}|${ticker}`;
};

const prepareDataSearchRows = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) => ({
    ...row,
    RowId: buildDataSearchRowId(row),
  }));
};

const names = [
  "Date",
  "Ticker",
  "Name",
  "Close",
  "CloseAtPredictionRelease",

  "DailyPricePrediction",
  "DailyPredictedGainLossPct",
  "DailyReturn",
  "DailyReturnPrediction",
  "DailyPercentDelta",
  "DailyAccuracy",

  "TwoDayPricePrediction",
  "TwoDayPredictedGainLossPct",
  "TwoDayPredictionDate",
  "TwoDayReturn",
  "TwoDayReturnPrediction",
  "TwoDayPercentDelta",
  "TwoDayAccuracy",

  "WeeklyPricePrediction",
  "WeeklyPredictedGainLossPct",
  "WeeklyPredictionDate",
  "WeeklyReturn",
  "WeeklyReturnPrediction",
  "WeeklyPercentDelta",
  "WeeklyAccuracy",

  "TwoWeekPricePrediction",
  "TwoWeekPredictedGainLossPct",
  "TwoWeekPredictionDate",
  "TwoWeekReturn",
  "TwoWeekReturnPrediction",
  "TwoWeekPercentDelta",
  "TwoWeekAccuracy",

  "MonthlyPricePrediction",
  "MonthlyPredictedGainLossPct",
  "MonthlyPredictionDate",
  "MonthlyReturn",
  "MonthlyReturnPrediction",
  "MonthlyPercentDelta",
  "MonthlyAccuracy",

  "TwoMonthPricePrediction",
  "TwoMonthPredictedGainLossPct",
  "TwoMonthPredictionDate",
  "TwoMonthReturn",
  "TwoMonthReturnPrediction",
  "TwoMonthPercentDelta",
  "TwoMonthAccuracy",

  "ThreeMonthPricePrediction",
  "ThreeMonthPredictedGainLossPct",
  "ThreeMonthPredictionDate",
  "ThreeMonthReturn",
  "ThreeMonthReturnPrediction",
  "ThreeMonthPercentDelta",
  "ThreeMonthAccuracy",

  "SixMonthPricePrediction",
  "SixMonthPredictedGainLossPct",
  "SixMonthPredictionDate",
  "SixMonthReturn",
  "SixMonthReturnPrediction",
  "SixMonthPercentDelta",
  "SixMonthAccuracy",

  "YearlyPricePrediction",
  "YearlyPredictedGainLossPct",
  "YearlyPredictionDate",
  "YearlyReturn",
  "YearlyReturnPrediction",
  "YearlyPercentDelta",
  "YearlyAccuracy",

  "Open",
  "Low",
  "High",
  "Volume"


];

const dataSearchHeaders = {
  Close: 'Close Price',
  CloseAtPredictionRelease: 'Release Close Price',
};

const types = (name) => ['Name', 'Ticker'].includes(name) ? 'string' : name.endsWith('Date') ? 'date' : 'number';

const params = (name, tickers, onSortInfo) => {
  const type = types(name);
  const result = {
    name,
    header: dataSearchHeaders[name] || getHeaderName(name),
    type,
    // Data Search is a wide, information-dense grid; give columns enough room so text isn't clipped.
    minWidth: 280,
    textAlign: 'center',
    headerAlign: 'center',
    // headerDOMProps: {
    //   onClick: (event, props) => onSortInfo(event, props.name),
    // },
  };
  if (name === 'CloseAtPredictionRelease') {
    return {
      ...result,
      filterEditor: NumberFilter,
      filterEditorProps: (props, {index}) => ({placeholder: index === 1 ? 'Max' : 'Min', step: 0.01}),
      render: ({value}) => prettyAmount(value, 2),
    };
  }
  if (name === 'Ticker') {
    return {
      ...result,
      type: 'select',
      filterEditor: SelectFilter,
      filterEditorProps: {
        multiple: true,
        wrapMultiple: false,
        dataSource: (tickers || []).map(ticker => ({id: ticker, label: ticker})),
      }
    }
  }
  if (name === 'Name') {
    return {...result, textAlign: 'start'}
  }
  if (name.endsWith('PredictedGainLossPct')) {
    return {
      ...result,
      filterEditor: NumberFilter,
      filterEditorProps: (props, {index}) => ({placeholder: index === 1 ? 'Max' : 'Min', step: 0.001}),
      render: ({value}) => {
        if (!Number.isFinite(value)) {
          return '';
        }
        const color = gainLossTextColor(value);
        return (
          <span style={{
            fontVariantNumeric: 'tabular-nums',
            color,
          }}>
            {formatGainLossPct(value)}
          </span>
        );
      },
    };
  }
  if (type === 'number') {
    return {
      ...result,
      filterEditor: NumberFilter,
      filterEditorProps: (props, {index}) => ({placeholder: index === 1 ? 'Max' : 'Min', step: 0.01}),
    };
  }
  if (type === 'date') {
    return {
      ...result,
      dateFormat: 'YYYY-MM-DD',
      filterEditor: DateFilter,
      render: ({value, cellProps: {dateFormat}}) => value && moment.utc(value).format(dateFormat),
    };
  }
  return result;
}

const Search = () => {
  const ref = React.useRef();
  const { push } = useHistory();
  const [gridRef, setGridRef] = React.useState();
  // const [modal, setModal] = React.useState(true);
  const [align, setAlign] = React.useState(false);
  const {payload, execute} = useApi(CONTROLLER.TICKERS_LOAD);
  const {execute: executeReport, loading: exportLoading} = useApi(CONTROLLER.REPORTS);
  const {store: {paid, advanced}} = React.useContext(StoreContext);
  
  React.useEffect(() => {
    if (paid === false) {
      push("/pricing-plans");
    }
  }, [paid, push]);

  React.useEffect(() => {
    if (!payload) {
      execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  const columns = React.useMemo(
    () => {
      const paid_list = ['Weekly', 'TwoDay', 'Daily'];
      const advanced_list = ['Yearly', 'SixMonth', 'ThreeMonth', 'TwoMonth', 'Monthly', 'TwoWeek'];
      let temp = [...names];
      if (!advanced) {
        temp = temp.filter(name => !advanced_list.find(period => name.startsWith(period)));
      }
      if (!paid) {
        temp = temp.filter(name => !paid_list.find(period => name.startsWith(period)));
      }
      return temp.map(name => params(name, (payload || {}).data))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payload, paid, advanced],
  );

  const defaultSortInfo = React.useMemo(() => ([
    {name: "Date", dir: -1},
    {name: "Ticker", dir: 1},
  ]), []);

  const defaultFilterValue = React.useMemo(() => columns.map(({name, type}) => ({
    name,
    type,
    operator: type === 'string' ? 'contains' : type === 'select' ? 'inlist' : 'inrange',
    value: null,
  })), [columns]);

  const style = React.useMemo(() => {
    return {
      minHeight: 700,
      // Leave extra room at the bottom so the grid footer/pagination doesn't get cut off.
      height: 'clamp(700px, calc(100vh - 530px), 2000px)',
      zIndex: 100,
    };
  }, []);

  const onExportCsv = React.useCallback(async () => {
    if (ref.current?.validate && !ref.current.validate()) return;
    const computed = gridRef?.current;
    if (!computed) return;

    const reportName = align ? 'dataSearchAlign' : 'dataSearch';
    const sortInfo = computed.computedSortInfo;
    const filterValue = computed.computedFilterValue;

    const visibleColumns = Array.isArray(computed.visibleColumns) ? computed.visibleColumns : [];
    const exportColumns = visibleColumns
      .filter((c) => c && c.name)
      .map((c) => ({
        name: c.name,
        header: (typeof c.header === 'string' && c.header.trim()) ? c.header : getHeaderName(c.name),
      }));

    const MAX_EXPORT_ROWS = 10000;
    const resp = await executeReport(
      {
        skip: 0,
        limit: MAX_EXPORT_ROWS,
        sortInfo,
        filterValue,
      },
      { urlPath: reportName },
    );

    let parsed;
    try {
      parsed = parseReportResponse(resp);
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('Export failed. Please try again.');
      return;
    }

    const rows = parsed.data;
    if (!rows.length) {
      // eslint-disable-next-line no-alert
      alert('No rows to export for the current filters.');
      return;
    }

    const formatted = rows.map((row) => {
      const out = {};
      exportColumns.forEach((col) => {
        out[col.name] = formatExportValue(col.name, row[col.name]);
      });
      return out;
    });

    const today = moment().format('YYYY-MM-DD');
    const filename = `data_search_${reportName}_${today}.csv`;
    const csv = buildCsv({ columns: exportColumns, rows: formatted });
    downloadTextFile({ contents: csv, filename, mimeType: 'text/csv;charset=utf-8' });

    const totalCount = Number(resp['data-total-count']);
    if (Number.isFinite(totalCount) && totalCount > MAX_EXPORT_ROWS) {
      // eslint-disable-next-line no-alert
      alert(`Exported the first ${MAX_EXPORT_ROWS.toLocaleString()} rows. Refine filters to export a smaller set.`);
    }
  }, [align, executeReport, gridRef]);

  return (
    <div className="stockSec dt-stockSec-search">
      <div className="container-fluid">
        <DashboardMenu/>
        <div className="dt-search-page">
        <div className="row">
          <div className="col-6">
            <h2>
              <span>Data Search</span>
            </h2>
          </div>
          <div className="col-6 text-right">
            <Field
              type="checkbox"
              name="align"
              value={align}
              label="Align Predictions"
              onChange={setAlign}
            />
            <Button
              style={{marginLeft: 30}}
              onClick={() => ref.current.save()}
              className="orangeBtnByPage"
              disabled={!gridRef?.current}
            >
              Save Table State
            </Button>
            {/*<Button*/}
            {/*  style={{marginLeft: 15}}*/}
            {/*  onClick={() => ref.current.reset()}*/}
            {/*  className="orangeBtnByPage"*/}
            {/*  disabled={!gridRef?.current}*/}
            {/*>*/}
            {/*  Reset Table State*/}
            {/*</Button>*/}
            <Button
              style={{marginLeft: 15}}
              onClick={() => gridRef.current.clearAllFilters()}
              className="orangeBtnByPage"
              disabled={!gridRef?.current}
            >
              Clear All Filters
            </Button>
            <Button
              style={{marginLeft: 15}}
              onClick={onExportCsv}
              className="blueBtnByPage"
              disabled={!gridRef?.current || exportLoading}
              title="Export the current filtered results to CSV (limited to 10,000 rows)"
            >
              Export to CSV
            </Button>
            {/*<Button*/}
            {/*  style={{marginLeft: 15}}*/}
            {/*  onClick={() => gridRef.current.clearAllFilters()}*/}
            {/*  className="orangeBtnByPage"*/}
            {/*  disabled={!gridRef?.current}*/}
            {/*>*/}
            {/*  Reset All Filters*/}
            {/*</Button>*/}
            {/*<Button*/}
            {/*  style={{marginLeft: 15}}*/}
            {/*  onClick={() => setModal(true)}*/}
            {/*  className="orangeBtnByPage"*/}
            {/*  disabled={!gridRef?.current}*/}
            {/*>*/}
            {/*  Choose Columns*/}
            {/*</Button>*/}
            {/*{modal && (*/}
            {/*  <ChooseColumns*/}
            {/*    handleClose={() => setModal(false)}*/}
            {/*    show={modal}*/}
            {/*  />*/}
            {/*)}*/}
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="dt-search-tip-row">
              <div className="dt-search-tip">
                Tip: hold Shift and click additional column headers to multi-sort.
              </div>
            </div>
            <Grid
              ref={ref}
              preference="dataSearch"
              report={align ? "dataSearchAlign" : "dataSearch"}

              enableFiltering
              defaultFilterValue={defaultFilterValue}
              enableColumnFilterContextMenu={false}

              idProperty="RowId"
              className="selectStockModal"
              columns={columns}
              prepareData={prepareDataSearchRows}

              rowHeight={35}

              sortable
              defaultSortInfo={defaultSortInfo}
              multiSort
              allowUnsort={false}

              pagination="remote"

              style={style}

              autoHide
              onReady={setGridRef}

              reorderColumns
              showColumnMenuTool
              showColumnMenuSortOptions={false}
              showColumnMenuGroupOptions={false}
              showColumnMenuFilterOptions={false}
              showColumnMenuLockOptions={false}

              scrollProps={{
                autoHide: false,
                scrollThumbWidth: 15,
                scrollThumbOverWidth: 20,
              }}
            />
          </div>
        </div>
        </div>
      </div>
    </div>
  )
};

export default Search;
